import React, { useState, useCallback, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';
import notify from '@/lib/notify';

/** Same Memory formats allow-list as DocumentsExplorer / document reader. */
const ACCEPTED_FORMAT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.doc',
  '.txt',
  '.md',
  '.eml',
  '.html',
  '.htm',
  '.rtf',
  '.pptx',
  '.xlsx',
  '.csv',
  '.tsv',
  '.odt',
  '.ods',
  '.odp',
  '.json',
  '.xml',
];

interface DocumentUploadProps {
  onFileSelect?: (file: File) => void | Promise<void>;
  onFilesSelect?: (files: File[]) => void | Promise<void>;
}

/**
 * Prevents double upload: only invokes onFileSelect once per file selection.
 * Resets the input in the next tick to avoid duplicate change events from value clearing.
 */
const DocumentUpload: React.FC<DocumentUploadProps> = ({ onFileSelect, onFilesSelect }) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const lastProcessedRef = useRef<{ name: string; size: number; lastModified: number } | null>(null);
  const uploadInProgressRef = useRef<boolean>(false);
  const inputId = useId();
  const acceptedFormatsLabel = ACCEPTED_FORMAT_EXTENSIONS.join(',');

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (files.length === 0) return;

      if (uploadInProgressRef.current) {
        event.target.value = '';
        return;
      }

      const file = files[0];
      const key = { name: file.name, size: file.size, lastModified: file.lastModified };
      if (
        lastProcessedRef.current &&
        lastProcessedRef.current.name === key.name &&
        lastProcessedRef.current.size === key.size &&
        lastProcessedRef.current.lastModified === key.lastModified
      ) {
        event.target.value = '';
        return;
      }
      lastProcessedRef.current = key;

      setSelectedFiles(files);
      uploadInProgressRef.current = true;
      if (onFilesSelect) {
        Promise.resolve(onFilesSelect(files))
          .then(() => {
            setSelectedFiles([]);
            lastProcessedRef.current = null;
          })
          .catch((err) => {
            console.error('DocumentUpload onFilesSelect error:', err);
          })
          .finally(() => {
            uploadInProgressRef.current = false;
          });
      } else if (onFileSelect) {
        Promise.resolve(
          files.reduce<Promise<void>>(
            (chain, currentFile) => chain.then(() => Promise.resolve(onFileSelect(currentFile)).then(() => undefined)),
            Promise.resolve()
          )
        )
          .then(() => {
            setSelectedFiles([]);
            lastProcessedRef.current = null;
          })
          .catch((err) => {
            console.error('DocumentUpload onFileSelect error:', err);
          })
          .finally(() => {
            uploadInProgressRef.current = false;
          });
      } else {
        uploadInProgressRef.current = false;
      }
      notify.success(t('documents.upload.filesSelected', { count: files.length }));

      setTimeout(() => {
        event.target.value = '';
      }, 0);
    },
    [onFileSelect, onFilesSelect, t]
  );

  const triggerFileInput = () => {
    document.getElementById(inputId)?.click();
  };

  return (
    <div className="text-right">
      <input
        type="file"
        id={inputId}
        accept={acceptedFormatsLabel}
        onChange={handleFileChange}
        className="hidden"
        multiple
      />
      <Button type="button" variant="outline" size="sm" onClick={triggerFileInput}>
        {t('documents.upload.button')}
      </Button>
      {selectedFiles.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground text-left">
          {t('documents.upload.filesSelected', { count: selectedFiles.length })}
        </p>
      )}
    </div>
  );
};

export default DocumentUpload;
